class QuickOrder {
  constructor() {
    this.maxRows = 5;
    this.rootUrl = window.Shopify?.routes?.root || '/';
    this.productCache = new Map();
  }

  async fetchProductByHandle(handle, signal) {
    if (this.productCache.has(handle)) {
      return this.productCache.get(handle);
    }

    const response = await fetch(
      `${this.rootUrl}products/${encodeURIComponent(handle)}.js`,
      { signal }
    );

    if (!response.ok) {
      return null;
    }

    const product = await response.json();
    this.productCache.set(handle, product);
    return product;
  }

  getVariantImageUrl(product, variant) {
    const featuredImage = variant?.featured_image;

    if (featuredImage) {
      if (typeof featuredImage === 'string') {
        return featuredImage.startsWith('//') ? `https:${featuredImage}` : featuredImage;
      }

      if (featuredImage.src) {
        return featuredImage.src;
      }
    }

    const productImage = product.featured_image || product.images?.[0];

    if (!productImage) return null;

    if (typeof productImage === 'string') {
      return productImage.startsWith('//') ? `https:${productImage}` : productImage;
    }

    return productImage.src || null;
  }

  extractSkuMatches(product, query) {
    const normalizedQuery = query.trim().toLowerCase();
    const matches = [];

    for (const variant of product.variants || []) {
      if (!variant.sku) continue;

      const normalizedSku = variant.sku.toLowerCase();
      if (!normalizedSku.includes(normalizedQuery)) continue;

      const imageUrl = this.getVariantImageUrl(product, variant);

      matches.push({
        sku: variant.sku,
        variantId: variant.id,
        variantTitle: variant.title,
        productTitle: product.title,
        available: variant.available,
        imageUrl,
        imageAlt: variant.featured_image?.alt || product.title,
      });
    }

    return matches;
  }

  async findVariantBySku(sku) {
    const trimmedSku = sku.trim();
    if (!trimmedSku) return null;

    const matches = await this.searchSkuSuggestions(trimmedSku, { limit: 20 });
    const normalizedSku = trimmedSku.toLowerCase();
    const exactMatch = matches.find((match) => match.sku.toLowerCase() === normalizedSku);

    if (!exactMatch) return null;

    return {
      variantId: exactMatch.variantId,
      title: exactMatch.productTitle,
      variantTitle: exactMatch.variantTitle,
      available: exactMatch.available,
    };
  }

  async searchSkuSuggestions(query, { limit = 6, signal } = {}) {
    const trimmed = query.trim();
    if (trimmed.length < 2) return [];

    const params = new URLSearchParams({
      q: trimmed,
      'resources[type]': 'product',
      'resources[limit]': '10',
      'resources[limit_scope]': 'each',
      'resources[options][fields]': 'title,variants.sku,variants.title',
    });

    const response = await fetch(`${this.rootUrl}search/suggest.json?${params.toString()}`, { signal });

    if (!response.ok) {
      throw new Error('Unable to search SKUs');
    }

    const data = await response.json();
    const products = data.resources?.results?.products || [];
    const handles = [...new Set(products.map((product) => product.handle).filter(Boolean))];

    if (!handles.length) return [];

    const fetchedProducts = await Promise.all(
      handles.map((handle) => this.fetchProductByHandle(handle, signal))
    );

    const normalizedQuery = trimmed.toLowerCase();
    const matches = [];

    for (const product of fetchedProducts) {
      if (!product) continue;
      matches.push(...this.extractSkuMatches(product, trimmed));
    }

    matches.sort((a, b) => {
      const aStarts = a.sku.toLowerCase().startsWith(normalizedQuery);
      const bStarts = b.sku.toLowerCase().startsWith(normalizedQuery);
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      return a.sku.localeCompare(b.sku);
    });

    const seen = new Set();
    return matches.filter((match) => {
      const key = match.sku.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).slice(0, limit);
  }

  async resolveRows(rows) {
    const resolved = [];
    const errors = [];

    for (let index = 0; index < rows.length; index++) {
      const row = rows[index];
      const sku = row.sku?.trim();
      const quantity = parseInt(row.qty, 10);

      if (!sku) continue;

      if (!quantity || quantity < 1) {
        errors.push({ index, message: 'Enter a quantity of 1 or more' });
        continue;
      }

      try {
        const variant = await this.findVariantBySku(sku);

        if (!variant) {
          errors.push({ index, message: 'SKU not found' });
          continue;
        }

        if (!variant.available) {
          errors.push({ index, message: 'Out of stock' });
          continue;
        }

        resolved.push({
          index,
          sku,
          quantity,
          variantId: variant.variantId,
          title: variant.title,
        });
      } catch (error) {
        errors.push({ index, message: 'Unable to look up SKU' });
      }
    }

    return { resolved, errors };
  }

  async addItemsToCart(items) {
    if (!items.length) {
      return { added: [], failed: [] };
    }

    const payload = {
      items: items.map((item) => ({
        id: item.variantId,
        quantity: item.quantity,
      })),
    };

    const response = await fetch(`${this.rootUrl}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (response.ok) {
      return { added: items, failed: [] };
    }

    const added = [];
    const failed = [];

    for (const item of items) {
      try {
        const singleResponse = await fetch(`${this.rootUrl}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: item.variantId, quantity: item.quantity }],
          }),
        });

        if (singleResponse.ok) {
          added.push(item);
        } else {
          const errorData = await singleResponse.json().catch(() => ({}));
          failed.push({
            index: item.index,
            message: errorData.description || 'Could not add to cart',
          });
        }
      } catch (error) {
        failed.push({ index: item.index, message: 'Could not add to cart' });
      }
    }

    return { added, failed };
  }

  async submit(rows) {
    const { resolved, errors } = await this.resolveRows(rows);

    if (resolved.length === 0 && errors.length === 0) {
      return {
        success: false,
        message: 'Enter at least one SKU to add items.',
        errors: [],
        addedCount: 0,
      };
    }

    if (resolved.length === 0) {
      return {
        success: false,
        message: 'Fix the errors below and try again.',
        errors,
        addedCount: 0,
      };
    }

    const { added, failed } = await this.addItemsToCart(resolved);
    const allErrors = [...errors, ...failed];

    if (window.cartManager) {
      await window.cartManager.updateCartDisplay();
    }

    if (added.length === 0) {
      return {
        success: false,
        message: 'No items were added to your cart.',
        errors: allErrors,
        addedCount: 0,
      };
    }

    const message =
      added.length === 1
        ? '1 item added to your cart.'
        : `${added.length} items added to your cart.`;

    return {
      success: true,
      message,
      errors: allErrors,
      addedCount: added.length,
    };
  }

  emptyRows() {
    return Array.from({ length: this.maxRows }, () => ({
      sku: '',
      qty: 1,
      error: '',
    }));
  }
}

window.quickOrder = new QuickOrder();

function registerSiteHeader() {
  Alpine.data('siteHeader', () => ({
    mobileMenuOpen: false,
    quickOrderOpen: false,
    rows: window.quickOrder.emptyRows(),
    loading: false,
    formMessage: '',
    formMessageType: '',
    activeSkuRow: null,
    skuSuggestions: [],
    skuSuggestSelected: -1,
    skuSuggestAbort: null,
    skuSuggestDebounce: null,

    openQuickOrder() {
      this.mobileMenuOpen = false;
      this.quickOrderOpen = true;
    },

    resetQuickOrderForm() {
      this.closeSkuSuggestions();
      this.rows = window.quickOrder.emptyRows();
      this.formMessage = '';
      this.formMessageType = '';
      this.loading = false;
    },

    closeSkuSuggestions() {
      if (this.skuSuggestDebounce) {
        clearTimeout(this.skuSuggestDebounce);
        this.skuSuggestDebounce = null;
      }

      if (this.skuSuggestAbort) {
        this.skuSuggestAbort.abort();
        this.skuSuggestAbort = null;
      }

      this.activeSkuRow = null;
      this.skuSuggestions = [];
      this.skuSuggestSelected = -1;
    },

    onSkuInput(index) {
      this.rows[index].error = '';
      this.activeSkuRow = index;
      this.skuSuggestSelected = -1;

      if (this.skuSuggestDebounce) {
        clearTimeout(this.skuSuggestDebounce);
      }

      const query = this.rows[index].sku?.trim() || '';
      if (query.length < 2) {
        this.skuSuggestions = [];
        return;
      }

      this.skuSuggestDebounce = setTimeout(() => {
        this.fetchSkuSuggestions(index, query);
      }, 300);
    },

    onSkuFocus(index) {
      this.activeSkuRow = index;
      const query = this.rows[index].sku?.trim() || '';

      if (query.length >= 2 && this.skuSuggestions.length === 0) {
        this.fetchSkuSuggestions(index, query);
      }
    },

    onSkuBlur() {
      setTimeout(() => {
        this.closeSkuSuggestions();
      }, 150);
    },

    async fetchSkuSuggestions(index, query) {
      if (this.skuSuggestAbort) {
        this.skuSuggestAbort.abort();
      }

      this.skuSuggestAbort = new AbortController();

      try {
        const suggestions = await window.quickOrder.searchSkuSuggestions(query, {
          signal: this.skuSuggestAbort.signal,
        });

        if (this.activeSkuRow === index) {
          this.skuSuggestions = suggestions;
          this.skuSuggestSelected = -1;
        }
      } catch (error) {
        if (error.name !== 'AbortError' && this.activeSkuRow === index) {
          this.skuSuggestions = [];
          this.skuSuggestSelected = -1;
        }
      }
    },

    selectSkuSuggestion(suggestion) {
      if (this.activeSkuRow === null) return;

      this.rows[this.activeSkuRow].sku = suggestion.sku;
      this.rows[this.activeSkuRow].error = '';
      this.closeSkuSuggestions();
    },

    onSkuKeydown(event, index) {
      const hasSuggestions =
        this.activeSkuRow === index && this.skuSuggestions.length > 0;

      if (!hasSuggestions) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.skuSuggestSelected = Math.min(
            this.skuSuggestSelected + 1,
            this.skuSuggestions.length - 1
          );
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.skuSuggestSelected = Math.max(this.skuSuggestSelected - 1, 0);
          break;
        case 'Enter':
          if (this.skuSuggestSelected >= 0) {
            event.preventDefault();
            this.selectSkuSuggestion(this.skuSuggestions[this.skuSuggestSelected]);
          }
          break;
        case 'Escape':
          event.preventDefault();
          this.closeSkuSuggestions();
          break;
      }
    },

    closeQuickOrder() {
      this.resetQuickOrderForm();
      this.quickOrderOpen = false;
    },

    clearQuickOrderErrors() {
      this.rows.forEach((row) => {
        row.error = '';
      });
      this.formMessage = '';
      this.formMessageType = '';
    },

    applyQuickOrderErrors(errors) {
      errors.forEach(({ index, message }) => {
        if (this.rows[index]) {
          this.rows[index].error = message;
        }
      });
    },

    async submitQuickOrder() {
      if (this.loading) return;

      this.clearQuickOrderErrors();
      this.loading = true;

      try {
        const result = await window.quickOrder.submit(this.rows);

        if (result.success) {
          window.location.assign(`${window.quickOrder.rootUrl}cart`);
          return;
        } else {
          this.formMessage = result.message;
          this.formMessageType = 'error';
          this.applyQuickOrderErrors(result.errors);
        }
      } catch (error) {
        this.formMessage = 'Something went wrong. Please try again.';
        this.formMessageType = 'error';
      } finally {
        this.loading = false;
      }
    },
  }));
}

if (window.Alpine) {
  registerSiteHeader();
} else {
  document.addEventListener('alpine:init', registerSiteHeader);
}
